/**
 * Auth redirect helpers — single source of truth for "where does a user
 * land after login?".
 *
 * Phase C2 (Faz 4): unify post-login navigation. Admin users should always
 * land on /admin; regular users on /user-dashboard. Previously, several
 * landing pages hard-coded '/user-dashboard' even for admins.
 *
 * Pure functions, no React, safe to import anywhere.
 */

/**
 * Resolve the canonical dashboard URL for a logged-in user.
 *
 * @param {Object|null} user - User object from AuthContext (or undefined).
 * @param {Object} [opts]
 * @param {string} [opts.adminPath='/admin']
 * @param {string} [opts.userPath='/user-dashboard']
 * @param {string} [opts.fallbackPath='/login']
 * @returns {string}
 */
export function getDashboardUrl(user, opts = {}) {
  const {
    adminPath = '/admin',
    userPath = '/user-dashboard',
    fallbackPath = '/login',
  } = opts;

  if (!user) return fallbackPath;
  return user.is_admin ? adminPath : userPath;
}

/**
 * Resolve the canonical signup URL. Optional lang query (TR/EN) is preserved
 * so e.g. the SoftwareShop can drop the user back where they were.
 *
 * @param {Object} [opts]
 * @param {string} [opts.path='/register']
 * @param {string} [opts.lang] - 'tr' | 'en' → appends ?lang=...
 * @param {string} [opts.next] - post-login return path
 * @returns {string}
 */
export function getSignupUrl(opts = {}) {
  const { path = '/register', lang, next } = opts;
  const params = [];
  if (lang) params.push(`lang=${lang}`);
  if (next) params.push(`next=${encodeURIComponent(next)}`);
  return params.length ? `${path}?${params.join('&')}` : path;
}

export default { getDashboardUrl, getSignupUrl };